import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Cloud, Server, Shield, Database, Network, Settings,
  CheckCircle, Clock, AlertTriangle, MessageSquare, 
  ArrowRight, Download, FileText, Play, Book, Users,
  Lock, Globe, Key, Target, Brain, ChevronRight,
  Award, Sparkles, ArrowLeft
} from 'lucide-react';
import ChatBot from '../../components/ChatBot';

const translations = {
  es: {
    title: 'AZ-900T00-A: Microsoft Azure Fundamentals',
    description: 'Aprende los conceptos fundamentales de la nube y los servicios de Azure',
    modules: {
      cloud: {
        title: 'Conceptos de la nube',
        duration: '2 días',
        description: 'Aprende los conceptos básicos de la computación en la nube y sus beneficios.'
      },
      services: {
        title: 'Servicios principales de Azure',
        duration: '3 días',
        description: 'Explora los servicios fundamentales de Azure como computación, redes y almacenamiento.'
      },
      security: {
        title: 'Seguridad y cumplimiento',
        duration: '2 días',
        description: 'Comprende la seguridad, privacidad y cumplimiento en Azure.'
      },
      pricing: {
        title: 'Precios y soporte',
        duration: '1 día',
        description: 'Aprende sobre los modelos de precios, SLAs y el ciclo de vida del servicio.'
      }
    },
    actions: {
      start: 'Comenzar módulo',
      continue: 'Continuar',
      askMentor: 'Preguntar al mentor'
    }
  },
  en: {
    title: 'AZ-900T00-A: Microsoft Azure Fundamentals',
    description: 'Learn cloud fundamentals and Azure services',
    modules: {
      cloud: {
        title: 'Cloud Concepts',
        duration: '2 days',
        description: 'Learn the basic concepts of cloud computing and its benefits.'
      },
      services: {
        title: 'Core Azure Services',
        duration: '3 days',
        description: 'Explore fundamental Azure services like compute, networking, and storage.'
      },
      security: {
        title: 'Security and Compliance',
        duration: '2 days',
        description: 'Understand security, privacy, and compliance in Azure.'
      },
      pricing: {
        title: 'Pricing and Support',
        duration: '1 day',
        description: 'Learn about pricing models, SLAs, and service lifecycle.'
      }
    },
    actions: {
      start: 'Start module',
      continue: 'Continue',
      askMentor: 'Ask mentor'
    }
  }
};

const modules = [
  {
    id: 'cloud',
    title: 'Cloud Concepts',
    description: 'Learn the basic concepts of cloud computing and its benefits',
    duration: '2 days',
    icon: Cloud,
    path: '/training/az-900/cloud',
    status: 'available'
  },
  {
    id: 'services',
    title: 'Core Azure Services',
    description: 'Explore fundamental Azure services',
    duration: '3 days',
    icon: Server,
    path: '/training/az-900/services',
    status: 'locked'
  },
  {
    id: 'security',
    title: 'Security and Compliance',
    description: 'Understand security, privacy, and compliance',
    duration: '2 days',
    icon: Shield,
    path: '/training/az-900/security',
    status: 'locked'
  },
  {
    id: 'pricing',
    title: 'Pricing and Support',
    description: 'Learn about pricing models and support options',
    duration: '1 day',
    icon: Database,
    path: '/training/az-900/pricing',
    status: 'locked'
  }
];

const AzureFundamentals = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const [completedModules, setCompletedModules] = useState<string[]>([]);
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  // Load completed modules from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('completedModules-az900');
    if (saved) {
      setCompletedModules(JSON.parse(saved));
    }
  }, []);

  // Save completed modules to localStorage
  useEffect(() => {
    localStorage.setItem('completedModules-az900', JSON.stringify(completedModules));
  }, [completedModules]);

  const isModuleAvailable = (index: number) => {
    if (index === 0) return true;
    return completedModules.includes(modules[index - 1].id);
  };

  const getModuleProgress = (moduleId: string) => {
    if (completedModules.includes(moduleId)) return 100;
    return 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white shadow-lg mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{t.title}</h1>
              <p className="mt-4 text-white/90 max-w-3xl">{t.description}</p>
            </div>
            <button
              onClick={() => setShowChatbot(true)}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg hover:bg-white/90 transition-colors flex items-center shadow-md hover:shadow-lg"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t.actions.askMentor}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Target className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Level</p>
                  <p className="font-medium">Beginner</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Duration</p>
                  <p className="font-medium">8 days</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Globe className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Languages</p>
                  <p className="font-medium">EN, ES</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center">
                <Award className="h-5 w-5 text-white/80 mr-2" />
                <div>
                  <p className="text-white/80 text-sm">Certification</p>
                  <p className="font-medium">AZ-900</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 gap-6">
          {modules.map((module, index) => {
            const isAvailable = isModuleAvailable(index);
            const progress = getModuleProgress(module.id);

            return (
              <div
                key={module.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <module.icon className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{module.title}</h3>
                      <p className="text-foreground/70">{module.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-foreground/50 mr-2" />
                    <span className="text-sm text-foreground/70">{module.duration}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-foreground/70">Progress</span>
                    <span className="text-sm font-medium text-foreground">{progress}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div
                      className="h-2 bg-accent rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  {!isAvailable ? (
                    <div className="flex items-center text-foreground/50">
                      <Lock className="h-5 w-5 mr-2" />
                      <span>Complete previous module first</span>
                    </div>
                  ) : progress === 100 ? (
                    <button
                      onClick={() => module.path && navigate(module.path)}
                      className="inline-flex items-center text-accent hover:text-accent/90"
                    >
                      Review
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => module.path && navigate(module.path)}
                      className="inline-flex items-center text-accent hover:text-accent/90"
                    >
                      {progress > 0 ? t.actions.continue : t.actions.start}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chatbot */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="azure-admin"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AzureFundamentals;